import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { X, Plus, Users, Globe, UsersThree, WhatsappLogo, Copy, Check } from "@phosphor-icons/react";

/**
 * Iter208.53 → Iter208.58 — Modal creazione appuntamento CONDIVISO usabile
 * dalla vista globale del calendario.
 *
 * Iter208.58 changes:
 * - Rimosso il dropdown "Immobile" in cima (ridondante).
 * - "Luogo appuntamento" ora contiene tutti gli immobili del pro
 *   formattati come "Via <indirizzo> <civico>, <città>", + opzione
 *   "Altro (testo libero)" e "Nessun luogo specifico". Campo opzionale.
 * - Nuovo selettore "Condividi con": chip Team / Partecipanti esterni / Entrambi.
 * - Se "external" o "both" → mostra input dinamico email esterne.
 * - Backend invia email in base a `share_with`.
 */
function toLocalDateInput(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const dd = d.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function formatPropertyLabel(p) {
  const parts = [];
  if (p.company_toponimo || p.toponimo) parts.push(p.company_toponimo || p.toponimo);
  const addr = (p.address || p.title || "").trim();
  if (addr) parts.push(addr);
  const civ = (p.civico || p.company_civico || "").trim();
  if (civ) parts.push(civ);
  const head = parts.join(" ").trim();
  const city = (p.city || p.company_city || "").trim();
  return city ? `${head}, ${city}` : head || p._id;
}

const SHARE_OPTIONS = [
  { id: "team", label: "Team", icon: UsersThree, desc: "Solo il tuo team" },
  { id: "external", label: "Partecipanti esterni", icon: Globe, desc: "Chi non è in team" },
  { id: "both", label: "Entrambi", icon: Users, desc: "Team + esterni" },
];

export default function SharedAppointmentModal({
  open, onClose, properties = [], initialDate, studioAddress, onCreated,
  editAppointment = null,
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [expectedParticipants, setExpectedParticipants] = useState(2);
  // Iter208.58 — Luogo: "none" | "custom" | "prop:<pid>" | "studio"
  const [locationChoice, setLocationChoice] = useState("none");
  const [locationCustom, setLocationCustom] = useState("");
  const [shareWith, setShareWith] = useState("external");
  const [slots, setSlots] = useState([{ start_at: "", duration_min: 60 }, { start_at: "", duration_min: 60 }]);
  const [saving, setSaving] = useState(false);
  // Iter208.60 — Panel di condivisione post-creazione (link + WhatsApp + copy)
  const [sharePanel, setSharePanel] = useState(null); // { public_url, title, share_with }
  const [copied, setCopied] = useState(false);

  const isEdit = !!editAppointment;
  const todayStr = toLocalDateInput(new Date());

  // Iter208.60 — Reset SOLO all'apertura (open false → true), non ad ogni
  // re-render del parent che potrebbe cambiare `initialDate` per riferimento
  // e sovrascriverebbe il share panel appena mostrato.
  const wasOpenRef = React.useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      if (editAppointment) {
        // Iter208.62 — Modalità Edit: pre-compila dai dati esistenti
        setTitle(editAppointment.title || "");
        setDescription(editAppointment.description || "");
        setExpectedParticipants(editAppointment.expected_participants || 2);
        // Location choice mapping
        const lt = editAppointment.location_type || "property";
        if (lt === "custom") {
          setLocationChoice("custom");
          setLocationCustom(editAppointment.location_value || "");
        } else if (lt === "studio") {
          setLocationChoice("studio");
        } else if (editAppointment.property_id) {
          setLocationChoice(`prop:${editAppointment.property_id}`);
        } else {
          setLocationChoice("none");
        }
        setShareWith(editAppointment.share_with || "external");
        // Slots pre-fill (formato datetime-local)
        const existingSlots = (editAppointment.slots || []).map((s) => {
          const dt = s.start_at ? new Date(s.start_at) : null;
          const yyyyMmDdHhMm = dt ? `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}T${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}` : "";
          return { id: s.id, start_at: yyyyMmDdHhMm, duration_min: s.duration_min || 60 };
        });
        setSlots(existingSlots.length >= 2 ? existingSlots : [
          ...existingSlots,
          ...Array(2 - existingSlots.length).fill(null).map(() => ({ start_at: "", duration_min: 60 })),
        ]);
      } else {
        setTitle("");
        setDescription("");
        setExpectedParticipants(2);
        setLocationChoice("none");
        setLocationCustom("");
        setShareWith("external");
        const seed = toLocalDateInput(initialDate);
        setSlots([
          { start_at: seed ? `${seed}T10:00` : "", duration_min: 60 },
          { start_at: seed ? `${seed}T15:00` : "", duration_min: 60 },
        ]);
      }
      setSharePanel(null);
      setCopied(false);
    }
    wasOpenRef.current = open;
  }, [open, initialDate, editAppointment]);

  const showExternal = shareWith === "external" || shareWith === "both";

  const addSlot = () => setSlots((s) => [...s, { start_at: "", duration_min: 60 }]);
  const removeSlot = (i) => setSlots((s) => s.filter((_, k) => k !== i));
  const updateSlot = (i, patch) => setSlots((s) => s.map((x, k) => (k === i ? { ...x, ...patch } : x)));

  const anchorPropertyId = useMemo(() => {
    if (locationChoice.startsWith("prop:")) return locationChoice.slice(5);
    return properties[0]?._id || "";
  }, [locationChoice, properties]);

  const save = async () => {
    if (!anchorPropertyId) {
      toast.error("Aggiungi prima almeno un immobile");
      return;
    }
    if (!title.trim()) { toast.error("Inserisci un titolo"); return; }
    const validSlots = slots.filter((s) => s.start_at);
    if (validSlots.length < 2) { toast.error("Inserisci almeno 2 slot"); return; }
    // Iter208.62 — Blocca slot in date passate lato client.
    for (const s of validSlots) {
      const d = new Date(s.start_at);
      if (Number.isFinite(d.getTime()) && d < new Date(new Date().toDateString())) {
        toast.error("Non puoi proporre uno slot in data passata");
        return;
      }
    }
    if (Number(expectedParticipants) < 2) { toast.error("Servono almeno 2 partecipanti attesi"); return; }
    if (locationChoice === "custom" && !locationCustom.trim()) {
      toast.error("Inserisci l'indirizzo o il link del luogo");
      return;
    }

    // Map location choice → backend fields
    let location_type = "property";
    let location_value = "";
    if (locationChoice === "custom") {
      location_type = "custom"; location_value = locationCustom.trim();
    } else if (locationChoice === "studio") {
      location_type = "studio";
    } else if (locationChoice.startsWith("prop:")) {
      location_type = "property";
    } else {
      location_type = "property";
    }

    setSaving(true);
    try {
      const slotPayload = validSlots.map((s) => ({
        id: s.id || undefined,
        start_at: new Date(s.start_at).toISOString(),
        duration_min: Number(s.duration_min) || 60,
      }));
      if (isEdit) {
        // Iter208.62 — Modalità Edit: PUT /appointments/{aid}
        await api.put(`/appointments/${editAppointment._id}`, {
          title: title.trim(),
          description: description.trim(),
          expected_participants: Number(expectedParticipants),
          slots: slotPayload,
          location_type,
          location_value,
        });
        toast.success("Appuntamento aggiornato");
        onCreated?.({ ...editAppointment, title: title.trim() });
        onClose?.();
        return;
      }
      const r = await api.post(`/properties/${anchorPropertyId}/appointments`, {
        title: title.trim(),
        description: description.trim(),
        expected_participants: Number(expectedParticipants),
        slots: slotPayload,
        location_type,
        location_value,
        share_with: shareWith,
        external_emails: [],
      });
      const a = r.data?.appointment || {};
      a.public_url = r.data?.public_url;
      if (shareWith === "team" || shareWith === "both") {
        toast.success("Appuntamento creato — email inviata al team");
      } else {
        toast.success("Appuntamento creato");
      }
      onCreated?.(a);
      // Iter208.60 — Mostra panel di condivisione (link + WhatsApp) invece di
      // chiudere il modal: l'utente può condividere manualmente il link ai
      // partecipanti esterni.
      setSharePanel({
        public_url: a.public_url,
        title: title.trim(),
        share_with: shareWith,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la creazione");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="shared-appointment-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica appuntamento condiviso" : "Nuovo appuntamento condiviso"}</DialogTitle>
        </DialogHeader>

        {sharePanel ? (
          <SharePanel
            data={sharePanel}
            copied={copied}
            setCopied={setCopied}
            onClose={onClose}
          />
        ) : (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Titolo *</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Es. Riunione visita immobile"
              data-testid="shared-appt-title"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Descrizione</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Info per i partecipanti"
              data-testid="shared-appt-description"
            />
          </div>

          {/* Iter208.58 — Condividi con (nascosto in edit) */}
          {!isEdit && (
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">Condividi con *</label>
            <div className="grid grid-cols-3 gap-2" data-testid="shared-appt-share-with">
              {SHARE_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const active = shareWith === opt.id;
                return (
                  <button
                    type="button"
                    key={opt.id}
                    onClick={() => setShareWith(opt.id)}
                    className={
                      "px-2 py-2 rounded-md border text-xs font-semibold flex flex-col items-center gap-1 transition-colors " +
                      (active
                        ? "bg-purple-600 text-white border-purple-600 shadow"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
                    }
                    data-testid={`shared-appt-share-${opt.id}`}
                    aria-pressed={active}
                    title={opt.desc}
                  >
                    <Icon size={16} weight="bold" />
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          )}

          {/* Luogo appuntamento con lista immobili + Altro */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Luogo appuntamento (opzionale)</label>
            <select
              value={locationChoice}
              onChange={(e) => setLocationChoice(e.target.value)}
              className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="shared-appt-location-select"
            >
              <option value="none">— Nessun luogo specifico —</option>
              {studioAddress && (
                <option value="studio">Studio · {studioAddress}</option>
              )}
              {properties.map((p) => (
                <option key={p._id} value={`prop:${p._id}`}>{formatPropertyLabel(p)}</option>
              ))}
              <option value="custom">Altro (testo libero)</option>
            </select>
            {locationChoice === "custom" && (
              <Textarea
                value={locationCustom}
                onChange={(e) => setLocationCustom(e.target.value)}
                rows={2}
                placeholder="Es. Via Roma 12, Milano — oppure https://meet.google.com/xyz"
                className="mt-2"
                data-testid="shared-appt-location-custom"
              />
            )}
          </div>

          {/* Email esterne dinamiche — rimosso Iter208.60: dopo il salvataggio
              viene mostrato un panel con link + WhatsApp. */}

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Numero partecipanti attesi *</label>
            <Input
              type="number"
              min={2}
              max={20}
              value={expectedParticipants}
              onChange={(e) => setExpectedParticipants(e.target.value)}
              className="w-24"
              data-testid="shared-appt-participants"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-slate-600">Slot proposti (min. 2)</label>
              <button
                type="button"
                onClick={addSlot}
                className="text-xs font-semibold text-purple-700 hover:text-purple-900"
                data-testid="shared-appt-slot-add"
              >
                + Slot
              </button>
            </div>
            <div className="space-y-2">
              {slots.map((s, i) => {
                const [dPart, tPart] = (s.start_at || "").split("T");
                const setDate = (d) => updateSlot(i, { start_at: d ? `${d}T${tPart || "10:00"}` : "" });
                const setTime = (t) => updateSlot(i, { start_at: `${dPart || toLocalDateInput(new Date())}T${t}` });
                return (
                  <div key={i} className="flex items-center gap-2 flex-wrap sm:flex-nowrap" data-testid={`shared-appt-slot-${i}`}>
                    <Input
                      type="date"
                      value={dPart || ""}
                      onChange={(e) => setDate(e.target.value)}
                      min={todayStr}
                      className="flex-1 min-w-[130px]"
                      data-testid={`shared-appt-slot-date-${i}`}
                    />
                    <select
                      value={tPart || ""}
                      onChange={(e) => setTime(e.target.value)}
                      className="border rounded px-2 py-2 text-sm bg-white w-24"
                      data-testid={`shared-appt-slot-time-${i}`}
                    >
                      <option value="">--:--</option>
                      {Array.from({ length: 24 }).flatMap((_, h) =>
                        [0, 15, 30, 45].map((m) => {
                          const val = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
                          return <option key={val} value={val}>{val}</option>;
                        }),
                      )}
                    </select>
                    <select
                      value={s.duration_min}
                      onChange={(e) => updateSlot(i, { duration_min: Number(e.target.value) })}
                      className="border rounded px-2 py-2 text-sm bg-white"
                      data-testid={`shared-appt-slot-duration-${i}`}
                    >
                      <option value={30}>30 min</option>
                      <option value={60}>1 ora</option>
                      <option value={90}>1h 30'</option>
                      <option value={120}>2 ore</option>
                      <option value={180}>3 ore</option>
                    </select>
                    {slots.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSlot(i)}
                        className="text-red-500 hover:text-red-700 p-1"
                        data-testid={`shared-appt-slot-remove-${i}`}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="shared-appt-cancel">
              Annulla
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="shared-appt-save"
            >
              {saving ? "Salvo…" : (isEdit ? "Salva modifiche" : "Crea e invia")}
            </Button>
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ==================== SharePanel (Iter208.60) ====================
// Panel post-creazione: mostra il link pubblico, un bottone "Condividi su
// WhatsApp" (wa.me con testo precompilato) e un bottone Copy manuale.
function SharePanel({ data, copied, setCopied, onClose }) {
  const { public_url = "", title = "", share_with = "external" } = data || {};
  const waText = encodeURIComponent(
    `Ciao! Ti invito all'appuntamento "${title}". Scegli lo slot che preferisci qui: ${public_url}`,
  );
  const waHref = `https://wa.me/?text=${waText}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(public_url);
      setCopied(true);
      toast.success("Link copiato");
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      // fallback textarea
      const t = document.createElement("textarea");
      t.value = public_url;
      document.body.appendChild(t);
      t.select();
      try { document.execCommand("copy"); setCopied(true); toast.success("Link copiato"); }
      catch (_err) { toast.error("Impossibile copiare"); }
      document.body.removeChild(t);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-4" data-testid="shared-appt-share-panel">
      <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 text-sm text-emerald-800">
        Appuntamento creato con successo.
        {(share_with === "team" || share_with === "both") && (
          <span> Email inviata al team.</span>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Link condivisibile</label>
        <div className="flex items-stretch gap-2">
          <Input
            readOnly
            value={public_url}
            className="flex-1 font-mono text-xs bg-slate-50"
            data-testid="shared-appt-share-link"
            onClick={(e) => e.currentTarget.select()}
          />
          <Button
            type="button"
            variant="outline"
            onClick={copyLink}
            className="shrink-0"
            data-testid="shared-appt-share-copy"
            title="Copia link"
          >
            {copied ? <Check size={14} weight="bold" /> : <Copy size={14} weight="bold" />}
            <span className="ml-1 hidden sm:inline">{copied ? "Copiato" : "Copia"}</span>
          </Button>
        </div>
      </div>

      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 w-full h-10 rounded-md bg-[#25D366] hover:bg-[#1DA851] text-white font-semibold text-sm transition-colors shadow"
        data-testid="shared-appt-share-whatsapp"
      >
        <WhatsappLogo size={18} weight="fill" />
        Condividi su WhatsApp
      </a>

      <p className="text-[11px] text-slate-500 text-center">
        Il link resta valido fino alla scadenza dell'appuntamento. Chi lo apre potrà
        prenotare il proprio slot senza registrazione.
      </p>

      <div className="flex justify-end pt-2 border-t">
        <Button onClick={onClose} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="shared-appt-share-done">
          Fatto
        </Button>
      </div>
    </div>
  );
}

